import { App } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { getApiError, postWorkStart } from "tm-shared/api";

import { useStableRef } from "@/hooks";
import {
    QueueTaskStatus,
    useAsyncTaskQueue,
} from "@/hooks/core/useAsyncTaskQueue";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import {
    applyRequestQueueItemReplacements,
    deleteRequestQueueItem,
    patchRequestQueueItem,
    setRequestQueueRunning,
    stopRequestQueue,
    touchRequestQueue,
} from "@/store/slices/offline";
import { addFailedQueueItem } from "@/store/slices/ui";
import { setTaskCompleted } from "@/store/slices/work";
import {
    QueueCommandMap,
    QueueCommandType,
    RequestQueueItem,
} from "@/types/requestQueue";
import { isOnline } from "@/utils/network/isOnline";

import { createErrorMarker } from "./useRequestQueue.utils";
import { useRequestQueueCommands } from "./useRequestQueueCommands";

const MoveToFailed = createErrorMarker("MOVE_TO_FAILED");
const StopQueue = createErrorMarker("STOP_QUEUE");

const WORK_START_REQUIRED_ERROR_CODES = ["-32000", "-32020"];

const MAX_ALLOWED_LATENCY_MS = 2000;

const callHandler = <T extends QueueCommandType>(
    handlers: {
        [K in QueueCommandType]: (p: QueueCommandMap[K]) => Promise<unknown>;
    },
    type: T,
    params: QueueCommandMap[T],
) => handlers[type](params);

const applyPendingReplacements = <T,>(
    value: T,
    replacements?: Record<string, string>,
): T => {
    if (!replacements || Object.keys(replacements).length === 0) {
        return value;
    }

    let raw = JSON.stringify(value);

    for (const [replaceValue, actualValue] of Object.entries(replacements)) {
        raw = raw.replaceAll(replaceValue, actualValue);
    }

    return JSON.parse(raw) as T;
};

export const useRequestQueue = () => {
    const { notification } = App.useApp();

    const displayStopWarning = useCallback(() => {
        notification.warning({
            title: "Черга тимчасово зупинена",
            description:
                "Мережа відповідає занадто повільно, тому ми зупинили відправку, щоб уникнути помилок. Відновіть інтернет і повторіть спробу.",
        });
    }, [notification]);

    const dispatch = useAppDispatch();

    const handlers = useRequestQueueCommands();

    const offlineMode = useOfflineModeSelector();
    const requestQueue = useAppSelector((s) => s.offline.requestQueue);
    const isInitialDataLoading = useAppSelector(
        (s) => s.offline.isInitialDataLoading,
    );
    const requestQueueStatus = useAppSelector(
        (s) => s.offline.requestQueueStatus,
    );

    const requestQueueRef = useStableRef(requestQueue);

    const offlineModeRef = useRef(offlineMode);
    useEffect(() => {
        offlineModeRef.current = offlineMode;
    }, [offlineMode]);

    const [currentItem, setCurrentItem] = useState<RequestQueueItem | null>(
        null,
    );

    const processingRef = useRef(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const { queue, progress } = useAsyncTaskQueue();

    const setProcessing = useCallback(
        (value: boolean) => {
            processingRef.current = value;
            dispatch(setRequestQueueRunning(value));

            if (!value) setCurrentItem(null);
        },
        [dispatch],
    );

    useEffect(() => {
        if (!currentItem) return;

        dispatch(touchRequestQueue());
        dispatch(
            patchRequestQueueItem({
                id: currentItem.id,
                patch: { progress },
            }),
        );
    }, [progress, currentItem, dispatch]);

    const { lastTickAt, isRunning } = requestQueueStatus;

    useEffect(() => {
        const TIMEOUT = 3 * 60 * 1000; // 3 хвилини

        const intervalId = setInterval(() => {
            if (isRunning && currentItem && lastTickAt) {
                const timeSinceLastTick = Date.now() - lastTickAt;

                if (timeSinceLastTick > TIMEOUT) {
                    console.error(
                        "Queue processing timeout - зависла обробка черги",
                        {
                            currentItem,
                            timeSinceLastTick,
                        },
                    );

                    dispatch(
                        patchRequestQueueItem({
                            id: currentItem.id,
                            patch: { status: QueueTaskStatus.Error },
                        }),
                    );

                    setProcessing(false);

                    notification.error({
                        title: "Обробка черги зупинена",
                        description:
                            "Процес обробки завис і був автоматично зупинений",
                    });
                }
            }
        }, 60000);

        return () => clearInterval(intervalId);
    }, [
        notification,
        currentItem,
        dispatch,
        setProcessing,
        isRunning,
        lastTickAt,
    ]);

    const processItem = useCallback(
        async (item: RequestQueueItem) => {
            const lastErrorIndex = item?.progress?.lastErrorIndex;
            const lastCompletedIndex = item?.progress?.lastCompletedIndex;

            const successStartIndex =
                lastCompletedIndex !== undefined ? lastCompletedIndex + 1 : 0;

            const startFromIndex = lastErrorIndex ?? successStartIndex;

            // Deep copy — mutate locally, do not touch the store directly.
            // Exception: after successful resolveOutputs — explicitly patch the store
            // so that after restart commands already have real values.
            const localItem = structuredClone(item);

            queue.clear();

            for (let i = 0; i < localItem.commands.length; i++) {
                // Capture index instead of command — to read the актуal
                // localItem.commands[index] at execution time,
                // after possible replacements from previous commands
                const index = i;

                const cmd = localItem.commands[index];

                if (!cmd) continue;

                queue.add(cmd.title, async () => {
                    const moveToFailedData = cmd.options?.moveToFailed ?? [];
                    const ignoreErrorCodes =
                        cmd.options?.ignoreErrorCodes ?? [];

                    try {
                        const isGoodLatency = await isOnline(
                            MAX_ALLOWED_LATENCY_MS,
                        );

                        if (!isGoodLatency) {
                            throw StopQueue.mark(
                                new Error(
                                    `STOP_QUEUE: high latency ${MAX_ALLOWED_LATENCY_MS}ms`,
                                ),
                            );
                        }

                        const normalizedParams = applyPendingReplacements(
                            cmd.params,
                            localItem.pendingReplacements,
                        );

                        if (normalizedParams !== cmd.params) {
                            cmd.params = normalizedParams;
                        }

                        let result;

                        try {
                            result = await callHandler(
                                handlers,
                                cmd.type,
                                normalizedParams,
                            );
                        } catch (error) {
                            const { code } = getApiError(error);
                            const userId = item.metadata?.user?.userId;
                            const roleSlug = item.metadata?.user?.roleSlug;

                            const isWorkStartRequired =
                                cmd.type === "postTaskComplete" &&
                                WORK_START_REQUIRED_ERROR_CODES.includes(code);
                            const hasRequiredUserMetadata = !!(
                                userId && roleSlug
                            );

                            if (
                                isWorkStartRequired &&
                                hasRequiredUserMetadata
                            ) {
                                await postWorkStart({ userId, roleSlug });
                                result = await callHandler(
                                    handlers,
                                    cmd.type,
                                    normalizedParams as QueueCommandMap[typeof cmd.type],
                                );
                            } else {
                                throw error;
                            }
                        }

                        // If the command has resolveOutputs — after successful execution
                        // replace values in all subsequent commands
                        if (cmd.resolveOutputs?.length && result) {
                            const replacements: Record<string, string> = {};

                            for (const {
                                extractPath,
                                replaceValue,
                            } of cmd.resolveOutputs) {
                                const extracted = extractPath
                                    .split(".")
                                    .reduce(
                                        (acc, key) =>
                                            acc != null
                                                ? (
                                                      acc as Record<
                                                          string,
                                                          unknown
                                                      >
                                                  )[key]
                                                : undefined,
                                        result as unknown,
                                    );

                                if (extracted == null) continue;

                                const realValue =
                                    typeof extracted === "string"
                                        ? extracted
                                        : JSON.stringify(extracted);

                                if (replaceValue === realValue) continue;

                                replacements[replaceValue] = realValue;
                            }

                            if (Object.keys(replacements).length) {
                                localItem.commands = applyPendingReplacements(
                                    localItem.commands,
                                    replacements,
                                );

                                localItem.metadata = applyPendingReplacements(
                                    localItem.metadata,
                                    replacements,
                                );

                                localItem.pendingReplacements = {
                                    ...localItem.pendingReplacements,
                                    ...replacements,
                                };

                                dispatch(
                                    applyRequestQueueItemReplacements({
                                        id: item.id,
                                        replacements,
                                    }),
                                );
                            }
                        }

                        if (cmd.type === "postTaskComplete") {
                            dispatch(
                                setTaskCompleted({
                                    taskId: item?.metadata?.taskId ?? "",
                                    targetId: item?.metadata?.pointId ?? "",
                                }),
                            );
                        }
                    } catch (error) {
                        if (StopQueue.is(error)) throw error;

                        const stopOnError = cmd.options?.stopOnError ?? true;
                        if (!stopOnError) return;

                        const { code } = getApiError(error);

                        const ignoreError = ignoreErrorCodes.some(
                            (errorCode) => String(errorCode) === String(code),
                        );
                        if (ignoreError) return;

                        const moveToFailed = moveToFailedData.find(
                            (x) => String(x.code) === String(code),
                        );

                        if (moveToFailed) {
                            if (cmd.type === "postTaskComplete") {
                                dispatch(
                                    setTaskCompleted({
                                        taskId: item?.metadata?.taskId ?? "",
                                        targetId: item?.metadata?.pointId ?? "",
                                    }),
                                );
                            }

                            notification.error({ title: moveToFailed.label });
                            throw MoveToFailed.mark(error);
                        }

                        throw error;
                    }
                });
            }

            try {
                await queue.runSuccessively({
                    startFromIndex,
                    stopOnError: item?.options?.stopOnError ?? true,
                });

                return {
                    ok: true,
                    commandsRevision: localItem.commandsRevision,
                };
            } catch (error) {
                if (StopQueue.is(error)) throw error;
                if (MoveToFailed.is(error))
                    return { ok: false, moveToFailed: true };
                return { ok: false };
            }
        },
        [notification, dispatch, handlers, queue],
    );

    useEffect(() => {
        if (offlineMode) return;

        if (
            requestQueue.length === 0 ||
            processingRef.current ||
            isInitialDataLoading
        ) {
            return;
        }

        setProcessing(true);

        const snapshot = requestQueue.filter(
            ({ status }) => status === QueueTaskStatus.Pending,
        );

        (async () => {
            try {
                for (let idx = 0; idx < snapshot.length; idx++) {
                    const online = await isOnline(MAX_ALLOWED_LATENCY_MS);
                    if (!online || offlineModeRef.current) {
                        displayStopWarning();

                        dispatch(stopRequestQueue());
                        break;
                    }

                    const item = snapshot[idx];
                    if (!item) continue;

                    if (!isMountedRef.current) return;

                    setCurrentItem(item);

                    try {
                        const { ok, moveToFailed, commandsRevision } =
                            await processItem(item);

                        if (ok) {
                            const actualItem = requestQueueRef.current.find(
                                (x) => x.id === item.id,
                            );

                            if (
                                !!actualItem &&
                                commandsRevision !== actualItem.commandsRevision
                            ) {
                                snapshot[idx] = actualItem;
                                idx--;
                                continue;
                            }

                            dispatch(deleteRequestQueueItem(item.id));
                            continue;
                        }

                        if (moveToFailed) {
                            dispatch(
                                addFailedQueueItem({
                                    ...item,
                                    progress: queue.progress,
                                }),
                            );
                            dispatch(deleteRequestQueueItem(item.id));
                            continue;
                        }

                        dispatch(
                            patchRequestQueueItem({
                                id: item.id,
                                patch: { status: QueueTaskStatus.Error },
                            }),
                        );
                    } catch (e) {
                        if (StopQueue.is(e)) {
                            displayStopWarning();

                            dispatch(stopRequestQueue());
                            break;
                        }

                        throw e;
                    } finally {
                        if (isMountedRef.current) setCurrentItem(null);
                    }
                }
            } finally {
                if (isMountedRef.current) setProcessing(false);
            }
        })();
    }, [
        dispatch,
        offlineMode,
        processItem,
        queue,
        requestQueue,
        isInitialDataLoading,
        setProcessing,
        requestQueueRef,
        displayStopWarning,
    ]);
};
